import * as React from 'react'
import { FlatList, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Select from '@components/select/Select'
import Modal from '@components/modal/Modal'
import Drawer from '@components/drawer/Drawer'
import Icon from '@components/icon/Icons'
import TextInput from '@components/textInput/TextInput'
import Button from '@components/button/Button'
import Checkbox from '@components/checkbox/Checkbox'

import sanitize from '@utils/Sanitizer'
import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

interface Option {
  value: string
  label: string
}

interface Props {
  children: React.ReactNode
  title: string
  subtitle?: string
  disabled?: boolean
  data: Option[]
  value: string[]
  onItemSelected: (item: string[]) => void
  selectAllEnabled?: boolean
  sectionTitle?: string
}

const SearchModal = ({
  data,
  onItemSelected,
  value,
  subtitle,
  title,
  selectAllEnabled,
  sectionTitle,
}: Omit<Props, 'children'>) => {
  const i18n = useI18n()

  const [search, setSearch] = React.useState('')
  const [selected, setSelected] = React.useState(value)
  const [, bp] = useBreakpoint()

  const items = React.useMemo(() => data.filter((d) => sanitize(d.label).includes(sanitize(search))), [data, search])

  const onSubmit = () => {
    onItemSelected(selected || '')
    Modal.close()
  }

  const onSelected = (val: string) => {
    const active = selected.includes(val)

    if (active) {
      setSelected(selected.filter((s) => s !== val))
    } else {
      setSelected([...selected, val])
    }
  }

  const onSelectAll = (allSelected: boolean, val: string[]) => {
    if (allSelected) {
      setSelected(selected.filter((s) => !val.includes(s)))
    } else {
      setSelected([...selected, ...val.filter((s) => !selected.includes(s))])
    }
  }

  const renderSectionHeader = () => {
    const allSelected = data.every((sp) => selected.includes(sp.value))

    if (!sectionTitle && !selectAllEnabled) {
      return null
    }

    return (
      <Section>
        {!!sectionTitle && <SectionTitle>{sectionTitle}</SectionTitle>}
        {selectAllEnabled && (
          <ChecklistContainer>
            <Checkbox
              label={i18n.t('screens.edl.spaces.selectAll')}
              onClick={() =>
                onSelectAll(
                  allSelected,
                  data.map((sp) => sp.value)
                )
              }
              value={allSelected}
              iconOnTheRight
            />
          </ChecklistContainer>
        )}
      </Section>
    )
  }

  const renderItem = ({ item }: { item: Option }) => {
    const focused = selected.includes(item.value)

    return (
      <SelectContainer key={item.value}>
        <Select
          label={item.label}
          onClick={() => onSelected(item.value)}
          focused={focused}
          selectStyle={!focused ? 'empty' : 'default'}
        />
      </SelectContainer>
    )
  }

  return (
    <SearchContainer>
      <InfoContainer>
        <ModalTitle>{title}</ModalTitle>
        {!!subtitle && <Current>{subtitle}</Current>}

        <Close activeOpacity={0.8} onPress={() => Modal.close()}>
          <Icon name="close" size={24} />
        </Close>
      </InfoContainer>
      <ListContainer>
        <InputContainer>
          <TextInput
            value={search}
            onChange={setSearch}
            placeholder={i18n.t('common.search')}
            left={{ name: 'search' }}
          />
        </InputContainer>

        <FlatList
          key={bp}
          data={items}
          renderItem={renderItem}
          keyExtractor={(_, idx) => `${idx}`}
          extraData={selected}
          ListHeaderComponent={renderSectionHeader()}
          ItemSeparatorComponent={ModalSeparator}
          numColumns={bp === 'phone' ? 1 : 2}
        />
      </ListContainer>

      <InfoContainer>
        <ButtonContainer>
          <Button label={i18n.t('common.validate')} status={!!selected ? 'active' : 'disabled'} onClick={onSubmit} />
        </ButtonContainer>
      </InfoContainer>
    </SearchContainer>
  )
}

const SearchDrawer = ({
  data,
  onItemSelected,
  value,
  title,
  selectAllEnabled,
  sectionTitle,
}: Omit<Props, 'children'>) => {
  const i18n = useI18n()

  const [selected, setSelected] = React.useState(value)
  const [search, setSearch] = React.useState('')

  const onSelected = (val: string) => {
    const active = selected.includes(val)

    if (active) {
      setSelected(selected.filter((s) => s !== val))
    } else {
      setSelected([...selected, val])
    }
  }

  const onSelectAll = (allSelected: boolean, val: string[]) => {
    if (allSelected) {
      setSelected(selected.filter((s) => !val.includes(s)))
    } else {
      setSelected([...selected, ...val.filter((s) => !selected.includes(s))])
    }
  }

  const renderSectionHeader = () => {
    const allSelected = data.every((sp) => selected.includes(sp.value))

    if (!sectionTitle && !selectAllEnabled) {
      return null
    }

    return (
      <Section>
        {!!sectionTitle && <SectionTitle>{sectionTitle}</SectionTitle>}
        {selectAllEnabled && (
          <ChecklistContainer>
            <Checkbox
              label={i18n.t('screens.edl.spaces.selectAll')}
              onClick={() =>
                onSelectAll(
                  allSelected,
                  data.map((sp) => sp.value)
                )
              }
              value={allSelected}
              iconOnTheRight
            />
          </ChecklistContainer>
        )}
      </Section>
    )
  }

  const renderItem = ({ item }: { item: Option }) => {
    const focused = selected.includes(item.value)

    return (
      <Cell onPress={() => onSelected(item.value)} activeOpacity={0.6}>
        <Item>{item.label}</Item>
        {focused && <Icon name="check" color={theme.colors.primary} size={24} />}
      </Cell>
    )
  }

  const validate = () => {
    onItemSelected(selected)
    Drawer.close()
  }

  const items = React.useMemo(() => data.filter((d) => sanitize(d.label).includes(sanitize(search))), [data, search])

  return (
    <MobileContainer>
      <Header>
        <SmallHeader onPress={() => Drawer.close()}>
          <Icon name="close" size={35} color={theme.colors.primary} />
        </SmallHeader>
        <CenterHeader>
          <DrawerTitle>{title}</DrawerTitle>
        </CenterHeader>
        <SmallHeader />
      </Header>
      <TopContainer>
        <TextInput
          onChange={setSearch}
          value={search}
          searchStyle="onLight"
          left={{ name: 'search' }}
          placeholder={i18n.t('screens.edl.spaces.placeholder')}
        />
      </TopContainer>
      <FlatList
        data={items}
        renderItem={renderItem}
        ItemSeparatorComponent={DrawerSeparator}
        keyExtractor={(item) => item.value}
        ListHeaderComponent={renderSectionHeader()}
      />
      <Footer>
        <Button
          label={i18n.t('screens.incident.PRESTA.drawer.validate')}
          onClick={validate}
          status={!!selected ? 'active' : 'disabled'}
        />
      </Footer>
    </MobileContainer>
  )
}

const ModalList = ({ children, disabled, ...rest }: Props) => {
  const [, bp] = useBreakpoint()

  const openSearch = () => {
    if (bp !== 'phone') {
      Modal.open({
        Content: () => <SearchModal {...rest} />,
      })
    } else {
      Drawer.open({
        render: () => <SearchDrawer {...rest} />,
        hideTarget: true,
      })
    }
  }

  return (
    <TouchableOpacity activeOpacity={0.9} onPress={openSearch} disabled={disabled}>
      {children}
    </TouchableOpacity>
  )
}

const ModalListService = {
  open: (props: Omit<Props, 'children'>, phone?: boolean) => {
    if (!phone) {
      Modal.open({
        Content: () => <SearchModal {...props} />,
      })
    } else {
      Drawer.open({
        render: () => <SearchDrawer {...props} />,
        hideTarget: true,
      })
    }
  },
  close: Modal.close,
}

export { ModalListService }
export default ModalList

//MODAL
const SearchContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'width: 880px'};
`

const InfoContainer = styled(View)`
  padding: 24px;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  position: relative;
`

const ModalTitle = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  font-size: 24px;
  margin-bottom: 8px;
`

const Current = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
`

const Close = styled(TouchableOpacity)`
  position: absolute;
  top: ${(props) => (props.theme.breakpoint === 'phone' ? 4 : 24)}px;
  right: ${(props) => (props.theme.breakpoint === 'phone' ? 4 : 24)}px;
  padding: 5px;
`

const ListContainer = styled(View)`
  padding: 24px;
  height: ${(props) => (props.theme.breakpoint === 'phone' ? 325 : 400)}px;
  background-color: ${(props) => props.theme.colors.background};
`

const InputContainer = styled(View)`
  margin-bottom: 24px;
`

const SelectContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 400px;`}
  margin-right: 24px;
`

const ModalSeparator = styled(View)`
  height: 24px;
`

const ButtonContainer = styled(View)`
  align-self: center;
  width: 275px;
`

//DRAWER
const TopContainer = styled(View)`
  padding: 0 21px 16px;
`

const Cell = styled(TouchableOpacity)`
  background-color: ${(props) => props.theme.colors.background};
  padding: 0 16px;
  height: 45px;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`

const Item = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const DrawerSeparator = styled(View)`
  height: 1px;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
`

const Footer = styled(View)`
  margin: 16px;
`

const Header = styled(View)`
  flex-direction: row;
  padding: 21px;
`

const SmallHeader = styled(TouchableOpacity)`
  flex: 1;
`

const CenterHeader = styled(View)`
  flex: 4;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
`

const DrawerTitle = styled(Text)<{ top?: number }>`
  ${(props) => props.theme.fonts.Headline};
  margin-top: ${(props) => (props.top ? props.top : 0)}px;
`

const MobileContainer = styled(View)`
  flex: 1;
  padding-bottom: ${(props) => props.theme.insets.bottom}px;
`

const Section = styled(View)`
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.backgroundSecondary : 'transparent'};
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  padding: 15px 16px;

  ${(props) => props.theme.breakpoint !== 'phone' && 'align-items: center;'}
`

const SectionTitle = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex: 1;`}
`

const ChecklistContainer = styled(View)`
  ${(props) => props.theme.breakpoint === 'phone' && 'margin-top: 8px;'}
`
